import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery } from '@tanstack/react-query';
import type { ChangeEntry } from '../api/changes';
import { changesApi } from '../api/changes';
import type { VehicleClass } from '../types';
import { CLASS_COLOURS, classLabel } from '../types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function localDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: 'numeric',
  });
}

function localDateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', spawncode: 'Spawncode', brand: 'Brand', category: 'Category',
  vehicle_class: 'Class', price: 'Price', link: 'Link', doors: 'Doors',
  seats: 'Seats', gears: 'Gears', top_speed: 'Top Speed',
  zero_to_sixty: '0–60', zero_to_hundred: '0–100', tiers: 'Tiers',
  dealership_id: 'Dealership', progress: 'Progress',
  boosting_exclusive: 'Boosting Excl.', rp_exclusive: 'RP Excl.',
};

interface FieldChange { field: string; from: string; to: string }

function parseAuditChanges(json: string | null): FieldChange[] | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Record<string, { old: unknown; new: unknown }>;
    if ('created' in parsed) return null;
    return Object.entries(parsed).map(([k, v]) => ({
      field: FIELD_LABELS[k] ?? k,
      from: String(v.old ?? '—'),
      to:   String(v.new ?? '—'),
    }));
  } catch { return null; }
}

function isCreation(json: string | null): boolean {
  if (!json) return false;
  try { return 'created' in JSON.parse(json); } catch { return false; }
}

function trunc(s: string, max = 55): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Changes() {
  const listRef    = useRef<HTMLDivElement>(null);
  const contentRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['changes', 30],
    queryFn:  () => changesApi.list(30),
  });

  const vehicles = data?.vehicles ?? [];

  // Stagger entrance for vehicle rows
  useGSAP(() => {
    if (!listRef.current || vehicles.length === 0) return;
    const rows = listRef.current.querySelectorAll('.change-row');
    gsap.from(rows, { y: 14, opacity: 0, stagger: 0.035, duration: 0.38, ease: 'power2.out' });
  }, [vehicles.length]);

  const toggle = (id: number) => {
    const el = contentRefs.current.get(id);
    if (!el) return;
    if (expandedIds.has(id)) {
      gsap.to(el, { height: 0, duration: 0.22, ease: 'power2.in' });
      setExpandedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    } else {
      gsap.to(el, { height: 'auto', duration: 0.3, ease: 'power2.out' });
      setExpandedIds(prev => new Set([...prev, id]));
    }
  };

  return (
    <div className="min-h-screen sakura-bg">
      <div className="max-w-screen-xl mx-auto px-4 py-6 space-y-5">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-heading text-2xl uppercase tracking-widest font-bold" style={{ color: 'var(--text)' }}>
              Recent Changes
            </h1>
            <p className="font-body text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Last 30 days — field edits &amp; handling revisions, newest first
            </p>
          </div>
          {!isLoading && (
            <span className="font-body text-sm pb-0.5" style={{ color: 'var(--text-muted)' }}>
              <span className="font-heading" style={{ color: 'var(--text)' }}>{vehicles.length}</span>
              {' '}vehicle{vehicles.length !== 1 ? 's' : ''} with changes
            </span>
          )}
        </div>

        {/* ── Skeleton ────────────────────────────────────────────── */}
        {isLoading && (
          <div className="space-y-px">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse"
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
              />
            ))}
          </div>
        )}

        {/* ── Empty ───────────────────────────────────────────────── */}
        {!isLoading && vehicles.length === 0 && (
          <div
            className="border px-6 py-16 text-center"
            style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <p className="font-heading text-sm uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>
              No changes in the last 30 days
            </p>
          </div>
        )}

        {/* ── Vehicle list ─────────────────────────────────────────── */}
        <div ref={listRef} className="space-y-px">
          {vehicles.map(v => {
            const col        = CLASS_COLOURS[v.vehicle_class as VehicleClass] ?? { bg: '#374151', text: '#FFFFFF' };
            const isExpanded = expandedIds.has(v.vehicle_id);
            const displayName = v.name ?? v.spawncode;

            return (
              <div
                key={v.vehicle_id}
                className="change-row"
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
              >
                {/* ── Vehicle header row (clickable) ──────────────── */}
                <button
                  type="button"
                  onClick={() => toggle(v.vehicle_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                  style={{
                    borderLeft: `3px solid ${col.bg}`,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Class badge */}
                  <span
                    className="font-heading text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 flex-shrink-0"
                    style={{ background: col.bg, color: col.text }}
                  >
                    {classLabel(v.vehicle_class as VehicleClass)}
                  </span>

                  {/* Name + spawncode */}
                  <span className="font-heading text-sm flex-1 min-w-0 truncate" style={{ color: 'var(--text)' }}>
                    {displayName}
                    {v.name && (
                      <span className="font-body text-[11px] font-normal ml-2" style={{ color: 'var(--text-muted)' }}>
                        {v.spawncode}
                      </span>
                    )}
                  </span>

                  {/* Change count pill */}
                  <span
                    className="font-heading text-[10px] uppercase tracking-wide px-2 py-0.5 flex-shrink-0"
                    style={{
                      background: 'var(--surface)',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-strong)',
                    }}
                  >
                    {v.entries.length} {v.entries.length === 1 ? 'change' : 'changes'}
                  </span>

                  {/* Latest change date */}
                  <span
                    className="font-body text-xs flex-shrink-0 hidden md:block"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {localDateOnly(v.latest_change_at)}
                  </span>

                  {/* Chevron */}
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                    style={{
                      color: 'var(--text-muted)',
                      flexShrink: 0,
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s ease',
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* ── Expandable entries list ──────────────────────── */}
                <div
                  ref={el => { if (el) contentRefs.current.set(v.vehicle_id, el); else contentRefs.current.delete(v.vehicle_id); }}
                  style={{ height: 0, overflow: 'hidden' }}
                >
                  <div className="border-t" style={{ borderColor: 'var(--border)' }}>
                    {v.entries.map((entry, i) => (
                      <EntryRow
                        key={`${entry.kind}-${entry.id}`}
                        entry={entry}
                        isLast={i === v.entries.length - 1}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

// ── Entry Row ─────────────────────────────────────────────────────────────────

function EntryRow({ entry, isLast }: { entry: ChangeEntry; isLast: boolean }) {
  const isHandling   = entry.kind === 'handling';
  const created      = !isHandling && isCreation(entry.changes);
  const fieldChanges = !isHandling && !created ? parseAuditChanges(entry.changes) : null;

  return (
    <div
      className="flex gap-3 px-5 py-3"
      style={{
        background: 'var(--surface)',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
      }}
    >
      {/* Kind badge */}
      <div className="flex-shrink-0 pt-0.5">
        <span
          className="font-heading text-[9px] uppercase tracking-wide px-1.5 py-0.5"
          style={
            isHandling
              ? { background: '#C8102E20', color: '#C8102E', border: '1px solid #C8102E40' }
              : created
              ? { background: '#22C55E20', color: '#16a34a', border: '1px solid #22C55E40' }
              : { background: 'var(--card)', color: 'var(--text-muted)', border: '1px solid var(--border-strong)' }
          }
        >
          {isHandling ? 'XML' : created ? 'New' : 'Edit'}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Who · when */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-heading text-xs" style={{ color: 'var(--text)' }}>
            {entry.changed_by}
          </span>
          <span style={{ color: 'var(--text-faint)' }}>·</span>
          <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
            {localDateTime(entry.occurred_at)}
          </span>
        </div>

        {/* What */}
        {isHandling ? (
          entry.change_note ? (
            <p className="font-body text-xs" style={{ color: 'var(--text)' }}>
              {entry.change_note}
            </p>
          ) : (
            <p className="font-body text-xs italic" style={{ color: 'var(--text-muted)' }}>
              Handling XML updated
            </p>
          )
        ) : created ? (
          <p className="font-body text-xs italic" style={{ color: 'var(--text-muted)' }}>
            Vehicle added to the system
          </p>
        ) : fieldChanges && fieldChanges.length > 0 ? (
          <div className="space-y-0.5">
            {fieldChanges.map((fc, i) => (
              <p key={i} className="font-body text-xs">
                <span
                  className="font-heading text-[9px] uppercase tracking-wide mr-1.5"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {fc.field}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{trunc(fc.from)}</span>
                <span className="mx-1" style={{ color: 'var(--text-faint)' }}>→</span>
                <span style={{ color: 'var(--text)' }}>{trunc(fc.to)}</span>
              </p>
            ))}
          </div>
        ) : null}

        {/* Audit note (if present alongside field changes) */}
        {!isHandling && !created && entry.note && (
          <p className="font-body text-xs italic" style={{ color: 'var(--text-muted)' }}>
            "{entry.note}"
          </p>
        )}
      </div>
    </div>
  );
}
